// src/pages/prodactions/production-summary/ProductionSummary.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Snackbar,
    Chip,
    Tabs,
    Tab,
    Tooltip,
    Menu,
    MenuItem,
    Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "../../../components/css/ded-style.css";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import Charts dari MUI X Charts
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";

// Import icons
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";

// Import merge settings components
import { useMergeSettings, MergeSettingsDialog } from "./setting/MergeSettings";

import { styled } from "@mui/material/styles";

// Import Production Table components
import { ProductionTable, bulanList } from "./ProductionTable";

// Import Summary Cards components
import { SummaryCards } from "./SummaryCards";

// Import Production Bar Chart components
import { ProductionBarChart } from "./ProductionBarChart";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Fungsi untuk format tanggal DD-MM-YYYY menjadi bulan dan tahun
const extractMonthYearFromDate = (dateString) => {
    if (!dateString || dateString === "-") {
        return { month: null, year: null };
    }

    try {
        // Coba parse format DD-MM-YYYY
        if (dateString.includes("-")) {
            const parts = dateString.split("-");
            if (parts.length === 3) {
                // Format DD-MM-YYYY
                if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                    return {
                        month: parseInt(parts[1]),
                        year: parseInt(parts[2]),
                    };
                }
                // Format YYYY-MM-DD
                else if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
                    return {
                        month: parseInt(parts[1]),
                        year: parseInt(parts[0]),
                    };
                }
            }
        }

        // Coba parse format DD/MM/YYYY
        if (dateString.includes("/")) {
            const parts = dateString.split("/");
            if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                return {
                    month: parseInt(parts[1]),
                    year: parseInt(parts[2]),
                };
            }
        }

        // Coba parse dengan Date object
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return {
                month: date.getMonth() + 1,
                year: date.getFullYear(),
            };
        }
    } catch (e) {
        console.error("Error extracting month/year:", e);
    }

    return { month: null, year: null };
};

// Fungsi untuk memparsing format Month Year seperti "Jan-2026" atau "Jan 2026"
const parseMonthYearFormat = (monthYearString) => {
    if (!monthYearString) return { month: null, year: null };

    try {
        // Mapping nama bulan
        const monthMap = {
            jan: 1,
            feb: 2,
            mar: 3,
            apr: 4,
            may: 5,
            jun: 6,
            jul: 7,
            aug: 8,
            sep: 9,
            oct: 10,
            nov: 11,
            dec: 12,
            january: 1,
            february: 2,
            march: 3,
            april: 4,
            may: 5,
            june: 6,
            july: 7,
            august: 8,
            september: 9,
            october: 10,
            november: 11,
            december: 12,
        };

        // Bersihkan string dan split
        const cleanString = monthYearString
            .replace(/[-\s]+/g, " ")
            .toLowerCase()
            .trim();
        const parts = cleanString.split(" ");

        let month = null;
        let year = null;

        for (const part of parts) {
            // Cek apakah part adalah angka (tahun)
            if (part.match(/^\d{4}$/)) {
                year = parseInt(part);
            } else {
                // Coba cek apakah part adalah nama bulan
                const monthPart = part.substring(0, 3); // Ambil 3 karakter pertama
                if (monthMap[monthPart]) {
                    month = monthMap[monthPart];
                } else if (monthMap[part]) {
                    month = monthMap[part];
                }
            }
        }

        return { month, year };
    } catch (e) {
        console.error("Error parsing month/year format:", e);
        return { month: null, year: null };
    }
};

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: (theme.vars ?? theme).palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

function ProductionSummary() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [allDataList, setAllDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // State untuk tab
    const [activeTab, setActiveTab] = useState(0);

    // Data untuk chart (kosongkan dulu)
    const [chartData, setChartData] = useState({
        xLabels: [],
        seriesData: [],
    });

    // State untuk filter tahun
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

    // State untuk daftar bulan yang aktif (Januari hingga bulan berjalan)
    const [activeMonths, setActiveMonths] = useState([]);

    // State untuk menu dropdown tahun
    const [yearAnchorEl, setYearAnchorEl] = useState(null);

    // State untuk data referensi
    const [distributionMapping, setDistributionMapping] = useState([]);
    const [tradeSectorsList, setTradeSectorsList] = useState([]);
    const [classificationCategoriesList, setClassificationCategoriesList] = useState([]);
    const [productSpecificationsList, setProductSpecificationsList] = useState([]);
    const [dailyProductionData, setDailyProductionData] = useState([]);

    // NEW: State untuk data production plan
    const [productionPlanData, setProductionPlanData] = useState([]);

    // State untuk ringkasan produksi
    const [productionSummary, setProductionSummary] = useState([]);

    // State untuk total berdasarkan kategori
    const [totals, setTotals] = useState({
        export: { rencana: 0, aktual: 0 },
        local: { rencana: 0, aktual: 0 },
        middleEast: { rencana: 0, aktual: 0 },
        asia: { rencana: 0, aktual: 0 },
        big: { rencana: 0, aktual: 0 },
        medium: { rencana: 0, aktual: 0 },
        small: { rencana: 0, aktual: 0 },
    });

    // STATE UNTUK PENGATURAN MERGE & CENTER - MENGGUNAKAN CUSTOM HOOK
    const { mergeSettings, saveMergeSettings } = useMergeSettings();

    // State untuk dialog pengaturan
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

    // Generate daftar tahun
    const currentYear = new Date().getFullYear();
    const yearList = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).filter((year) => year >= 2020);

    // Update activeMonths ketika selectedYear berubah
    useEffect(() => {
        const now = new Date();
        let maxMonth = 12; // Default ke Desember jika tahun yang dipilih bukan tahun berjalan

        // Jika tahun yang dipilih adalah tahun berjalan, batasi hingga bulan berjalan
        if (selectedYear === now.getFullYear()) {
            maxMonth = now.getMonth() + 1; // Bulan berjalan (1-12)
        }

        // Buat daftar bulan dari Januari hingga maxMonth
        const months = [];
        for (let i = 1; i <= maxMonth; i++) {
            months.push(i);
        }
        setActiveMonths(months);
    }, [selectedYear]);

    // Handler untuk tahun dropdown
    const handleYearClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        handleYearClose();
    };

    // Handler untuk settings - LANGSUNG BUKA DIALOG
    const handleSettingsClick = () => {
        setSettingsDialogOpen(true);
    };

    // Handler untuk menyimpan pengaturan
    const handleSaveSettings = (newSettings) => {
        // Update state utama melalui custom hook
        saveMergeSettings(newSettings);

        // Tampilkan notifikasi
        setSnackbar({
            open: true,
            message: "Pengaturan merge & center berhasil disimpan",
            severity: "success",
        });
    };

    // Fetch semua data yang diperlukan
    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                console.log("📥 Fetching all reference data...");

                // 1. Fetch Trade Sectors
                const tradeSectorsRef = ref(database, "db_productions/tb_trade_sectors");
                const tradeSectorsSnapshot = await onValuePromise(tradeSectorsRef);
                const tradeSectors = tradeSectorsSnapshot.val() || {};

                const tradeSectorsData = [];
                Object.keys(tradeSectors).forEach((key) => {
                    tradeSectorsData.push({
                        id: tradeSectors[key].sector_id || key,
                        name: tradeSectors[key].sector_name || "",
                        firebaseKey: key,
                    });
                });
                setTradeSectorsList(tradeSectorsData);

                // 2. Fetch Classification Categories
                const categoriesRef = ref(database, "db_productions/tb_classification_categories");
                const categoriesSnapshot = await onValuePromise(categoriesRef);
                const categories = categoriesSnapshot.val() || {};

                const categoriesData = [];
                Object.keys(categories).forEach((key) => {
                    categoriesData.push({
                        id: categories[key].classification_categories_id || key,
                        name: categories[key].classification_categories_name || "",
                        firebaseKey: key,
                    });
                });
                setClassificationCategoriesList(categoriesData);

                // 3. Fetch Product Specifications
                const specificationsRef = ref(database, "db_productions/tb_product_specifications");
                const specificationsSnapshot = await onValuePromise(specificationsRef);
                const specifications = specificationsSnapshot.val() || {};

                const specificationsData = [];
                Object.keys(specifications).forEach((key) => {
                    specificationsData.push({
                        id: specifications[key].product_specification_id || key,
                        name: specifications[key].product_specification_name || "",
                        firebaseKey: key,
                    });
                });
                setProductSpecificationsList(specificationsData);

                // 4. Fetch Distribution Mapping
                const mappingRef = ref(database, "db_productions/tb_distribution_mapping");
                const mappingSnapshot = await onValuePromise(mappingRef);
                const mapping = mappingSnapshot.val() || {};

                const mappingData = [];
                Object.keys(mapping).forEach((key) => {
                    const record = mapping[key];
                    mappingData.push({
                        firebaseKey: key,
                        distribution_mapping_id: record.distribution_mapping_id || key,
                        sector_id: record.sector_id || "",
                        classification_categories_id: record.classification_categories_id || "",
                        product_specification_id: record.product_specification_id || "",
                        material_code: record.material_code || "",
                    });
                });
                setDistributionMapping(mappingData);

                // 5. Fetch Daily Production Data
                const productionRef = ref(database, "db_productions/tb_daily_production");
                const productionSnapshot = await onValuePromise(productionRef);
                const productionData = productionSnapshot.val() || {};

                const productionArray = [];
                Object.keys(productionData).forEach((key) => {
                    const record = productionData[key];
                    if (record.packing_date && record.material_code && record.sum_of_qty) {
                        productionArray.push({
                            firebaseKey: key,
                            packing_date: record.packing_date || "",
                            material_code: record.material_code || "",
                            sum_of_qty: parseFloat(record.sum_of_qty) || 0,
                            sloc: record.sloc || "",
                        });
                    }
                });
                setDailyProductionData(productionArray);

                // 6. NEW: Fetch Production Plan Data
                const productionPlanRef = ref(database, "db_productions/tb_production_plan");
                const productionPlanSnapshot = await onValuePromise(productionPlanRef);
                const productionPlanData = productionPlanSnapshot.val() || {};

                const productionPlanArray = [];
                Object.keys(productionPlanData).forEach((key) => {
                    const record = productionPlanData[key];
                    productionPlanArray.push({
                        firebaseKey: key,
                        production_plan_id: record.production_plan_id || key,
                        product_specification_id: record.product_specification_id || "",
                        month_year: record.month_year || "",
                        production_plan: parseFloat(record.production_plan) || 0,
                        last_updated: record.last_updated || null,
                    });
                });
                setProductionPlanData(productionPlanArray);

                console.log("✅ All reference data fetched successfully");
                console.log(`📊 Trade Sectors: ${tradeSectorsData.length}`);
                console.log(`📊 Categories: ${categoriesData.length}`);
                console.log(`📊 Specifications: ${specificationsData.length}`);
                console.log(`📊 Distribution Mapping: ${mappingData.length}`);
                console.log(`📊 Daily Production: ${productionArray.length}`);
                console.log(`📊 Production Plan: ${productionPlanArray.length}`);
            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    // Effect untuk memproses ringkasan produksi dan menghitung total
    useEffect(() => {
        if (
            distributionMapping.length > 0 &&
            dailyProductionData.length > 0 &&
            tradeSectorsList.length > 0 &&
            classificationCategoriesList.length > 0 &&
            productSpecificationsList.length > 0 &&
            activeMonths.length > 0
        ) {
            console.log(`🔄 Processing production summary for year ${selectedYear} (${activeMonths.length} months)...`);

            // Filter production data berdasarkan tahun dari packing_date
            const filteredProduction = dailyProductionData.filter((item) => {
                if (!item.packing_date) return false;
                const { month, year } = extractMonthYearFromDate(item.packing_date);
                return year === selectedYear && activeMonths.includes(month);
            });

            console.log(`📦 Production data after year filter: ${filteredProduction.length} records`);

            // Buat Map untuk aggregasi berdasarkan material_code dan bulan
            const productionByMaterialAndMonth = new Map();

            filteredProduction.forEach((item) => {
                const materialCode = item.material_code;
                const qty = item.sum_of_qty || 0;
                const { month } = extractMonthYearFromDate(item.packing_date);

                if (materialCode && month) {
                    const key = `${materialCode}_${month}`;
                    if (productionByMaterialAndMonth.has(key)) {
                        productionByMaterialAndMonth.set(key, productionByMaterialAndMonth.get(key) + qty);
                    } else {
                        productionByMaterialAndMonth.set(key, qty);
                    }
                }
            });

            // Buat Map untuk mapping dari material_code ke distribution mapping
            const mappingByMaterial = new Map();
            distributionMapping.forEach((map) => {
                if (map.material_code) {
                    mappingByMaterial.set(map.material_code, map);
                }
            });

            // NEW: Buat Map untuk production plan berdasarkan product_specification_id dan bulan
            const productionPlanBySpecAndMonth = new Map();
            productionPlanData.forEach((plan) => {
                if (plan.product_specification_id && plan.month_year) {
                    const { month, year } = parseMonthYearFormat(plan.month_year);
                    if (month && year === selectedYear && activeMonths.includes(month)) {
                        const key = `${plan.product_specification_id}_${month}`;
                        productionPlanBySpecAndMonth.set(key, plan.production_plan);
                    }
                }
            });

            // Buat summary berdasarkan Trade Sector, Classification, Product Specification
            const summaryMap = new Map();

            // Inisialisasi total
            const newTotals = {
                export: { rencana: 0, aktual: 0 },
                local: { rencana: 0, aktual: 0 },
                middleEast: { rencana: 0, aktual: 0 },
                asia: { rencana: 0, aktual: 0 },
                big: { rencana: 0, aktual: 0 },
                medium: { rencana: 0, aktual: 0 },
                small: { rencana: 0, aktual: 0 },
            };

            // Loop melalui semua kombinasi material dan bulan
            productionByMaterialAndMonth.forEach((totalQty, key) => {
                const [materialCode, month] = key.split("_");
                const monthNum = parseInt(month);
                const mapping = mappingByMaterial.get(materialCode);

                if (mapping) {
                    // Cari nama-nama dari ID
                    const tradeSector = tradeSectorsList.find((ts) => ts.id === mapping.sector_id);
                    const classification = classificationCategoriesList.find(
                        (cc) => cc.id === mapping.classification_categories_id
                    );
                    const productSpec = productSpecificationsList.find(
                        (ps) => ps.id === mapping.product_specification_id
                    );

                    const sectorName = tradeSector?.name || mapping.sector_id || "-";
                    const classificationName = classification?.name || mapping.classification_categories_id || "-";
                    const productSpecName = productSpec?.name || mapping.product_specification_id || "-";

                    // Hitung total berdasarkan kategori
                    const sectorLower = sectorName.toLowerCase();
                    const classificationLower = classificationName.toLowerCase();
                    const productLower = productSpecName.toLowerCase();

                    // Kategorisasi Export/Local
                    if (
                        sectorLower.includes("export") ||
                        classificationLower.includes("export") ||
                        productLower.includes("export")
                    ) {
                        newTotals.export.aktual += totalQty;
                    } else if (
                        sectorLower.includes("local") ||
                        classificationLower.includes("local") ||
                        productLower.includes("local")
                    ) {
                        newTotals.local.aktual += totalQty;
                    }

                    // Kategorisasi MIDDLE EAST/ASIA
                    if (
                        sectorLower.includes("middle east") ||
                        classificationLower.includes("middle east") ||
                        productLower.includes("middle east")
                    ) {
                        newTotals.middleEast.aktual += totalQty;
                    } else if (
                        sectorLower.includes("asia") ||
                        classificationLower.includes("asia") ||
                        productLower.includes("asia")
                    ) {
                        newTotals.asia.aktual += totalQty;
                    }

                    // Kategorisasi BIG/MEDIUM/SMALL
                    if (
                        sectorLower.includes("big") ||
                        classificationLower.includes("big") ||
                        productLower.includes("big")
                    ) {
                        newTotals.big.aktual += totalQty;
                    } else if (
                        sectorLower.includes("medium") ||
                        classificationLower.includes("medium") ||
                        productLower.includes("medium")
                    ) {
                        newTotals.medium.aktual += totalQty;
                    } else if (
                        sectorLower.includes("small") ||
                        classificationLower.includes("small") ||
                        productLower.includes("small")
                    ) {
                        newTotals.small.aktual += totalQty;
                    }

                    // Buat key unik berdasarkan kombinasi sektor, klasifikasi, spesifikasi
                    const summaryKey = `${sectorName}|${classificationName}|${productSpecName}`;

                    if (!summaryMap.has(summaryKey)) {
                        // Inisialisasi objek dengan semua bulan
                        const newRow = {
                            tradeSector: sectorName,
                            classification: classificationName,
                            productSpecification: productSpecName,
                        };

                        // Inisialisasi semua bulan dengan nilai 0
                        activeMonths.forEach((m) => {
                            newRow[`rencana_${m}`] = 0;
                            newRow[`aktual_${m}`] = 0;
                        });

                        summaryMap.set(summaryKey, newRow);
                    }

                    // Update nilai aktual untuk bulan yang sesuai
                    const row = summaryMap.get(summaryKey);
                    row[`aktual_${monthNum}`] = (row[`aktual_${monthNum}`] || 0) + totalQty;
                }
            });

            // NEW: Tambahkan production plan ke summary dan hitung total rencana
            summaryMap.forEach((row, summaryKey) => {
                // Cari product specification ID berdasarkan nama
                const productSpec = productSpecificationsList.find((ps) => ps.name === row.productSpecification);

                if (productSpec) {
                    activeMonths.forEach((month) => {
                        const planKey = `${productSpec.id}_${month}`;
                        const planValue = productionPlanBySpecAndMonth.get(planKey) || 0;

                        // Set nilai rencana
                        row[`rencana_${month}`] = planValue;

                        // Tambahkan ke total rencana berdasarkan kategori
                        if (planValue > 0) {
                            const sectorLower = row.tradeSector.toLowerCase();
                            const classificationLower = row.classification.toLowerCase();
                            const productLower = row.productSpecification.toLowerCase();

                            // Kategorisasi Export/Local untuk rencana
                            if (
                                sectorLower.includes("export") ||
                                classificationLower.includes("export") ||
                                productLower.includes("export")
                            ) {
                                newTotals.export.rencana += planValue;
                            } else if (
                                sectorLower.includes("local") ||
                                classificationLower.includes("local") ||
                                productLower.includes("local")
                            ) {
                                newTotals.local.rencana += planValue;
                            }

                            // Kategorisasi MIDDLE EAST/ASIA untuk rencana
                            if (
                                sectorLower.includes("middle east") ||
                                classificationLower.includes("middle east") ||
                                productLower.includes("middle east")
                            ) {
                                newTotals.middleEast.rencana += planValue;
                            } else if (
                                sectorLower.includes("asia") ||
                                classificationLower.includes("asia") ||
                                productLower.includes("asia")
                            ) {
                                newTotals.asia.rencana += planValue;
                            }

                            // Kategorisasi BIG/MEDIUM/SMALL untuk rencana
                            if (
                                sectorLower.includes("big") ||
                                classificationLower.includes("big") ||
                                productLower.includes("big")
                            ) {
                                newTotals.big.rencana += planValue;
                            } else if (
                                sectorLower.includes("medium") ||
                                classificationLower.includes("medium") ||
                                productLower.includes("medium")
                            ) {
                                newTotals.medium.rencana += planValue;
                            } else if (
                                sectorLower.includes("small") ||
                                classificationLower.includes("small") ||
                                productLower.includes("small")
                            ) {
                                newTotals.small.rencana += planValue;
                            }
                        }
                    });
                }
            });

            // Konversi Map ke array dan sort
            const summaryArray = Array.from(summaryMap.values()).sort((a, b) => {
                if (a.tradeSector !== b.tradeSector) return a.tradeSector.localeCompare(b.tradeSector);
                if (a.classification !== b.classification) return a.classification.localeCompare(b.classification);
                return a.productSpecification.localeCompare(b.productSpecification);
            });

            console.log(
                `✅ Production summary generated: ${summaryArray.length} rows with ${activeMonths.length} months`
            );

            setProductionSummary(summaryArray);
            setDataList(summaryArray);
            setTotals(newTotals);
        }
    }, [
        selectedYear,
        activeMonths,
        distributionMapping,
        dailyProductionData,
        tradeSectorsList,
        classificationCategoriesList,
        productSpecificationsList,
        productionPlanData, // NEW: Tambahkan dependency
    ]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Memuat data produksi...</Typography>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    // Format judul berdasarkan tahun dan rentang bulan
    const getTitleText = () => {
        if (activeMonths.length === 0) return `Production Summary - ${selectedYear}`;

        const firstMonth = bulanList.find((b) => b.id === activeMonths[0])?.nama || "";
        const lastMonth = bulanList.find((b) => b.id === activeMonths[activeMonths.length - 1])?.nama || "";

        if (activeMonths.length === 1) {
            return `Production Summary - ${lastMonth} ${selectedYear}`;
        } else {
            return `Production Summary - ${firstMonth} - ${lastMonth} ${selectedYear}`;
        }
    };

    const getPeriodeText = () => {
        if (activeMonths.length === 0) return `${selectedYear}`;

        const firstMonth = bulanList.find((b) => b.id === activeMonths[0])?.nama || "";
        const lastMonth = bulanList.find((b) => b.id === activeMonths[activeMonths.length - 1])?.nama || "";

        if (activeMonths.length === 1) {
            return `${lastMonth} ${selectedYear}`;
        } else {
            return `${firstMonth} - ${lastMonth} ${selectedYear}`;
        }
    };

    // Debug info untuk empty state
    const debugInfo = {
        tradeSectorsList: tradeSectorsList.length,
        classificationCategoriesList: classificationCategoriesList.length,
        productSpecificationsList: productSpecificationsList.length,
        distributionMapping: distributionMapping.length,
        dailyProductionData: dailyProductionData.length,
        productionPlanData: productionPlanData.length, // NEW: Tambahkan production plan
        filteredProduction: dailyProductionData.filter((item) => {
            const { year } = extractMonthYearFromDate(item.packing_date);
            return year === selectedYear;
        }).length,
        activeMonths: activeMonths.map((m) => bulanList.find((b) => b.id === m)?.nama).join(", "),
    };

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0, mt: 2 }}>
                {/* HEADER DENGAN STYLE GYPSUMPERMONTH */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" color="text.primary">
                            Production Summary
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Periode: {getPeriodeText()}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Tahun dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Settings untuk Merge & Center - LANGSUNG BUKA DIALOG */}
                        <Tooltip title="Pengaturan Merge & Center">
                            <Chip
                                size="small"
                                color={
                                    mergeSettings.mergeTradeSector || mergeSettings.mergeClassification
                                        ? "info"
                                        : "default"
                                }
                                variant="outlined"
                                onClick={handleSettingsClick}
                                icon={<MergeTypeIcon />}
                                label="Merge"
                                deleteIcon={
                                    <ArrowDropDownOutlinedIcon
                                        sx={{
                                            transform: "rotate(0deg)",
                                            transition: "transform 0.2s",
                                        }}
                                    />
                                }
                                onDelete={handleSettingsClick}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Tabs untuk Bar Chart dan Tabel */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {activeTab === 0 ? (
                    <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid size={12}>
                            {/* Menggunakan komponen SummaryCards yang telah dipisah */}
                            <SummaryCards totals={totals} />
                        </Grid>
                        <Grid size={12}>
                            {/* Tampilan Bar Chart - MENGGUNAKAN KOMPONEN YANG TELAH DIPISAH */}
                            <ProductionBarChart
                                productionSummary={productionSummary}
                                activeMonths={activeMonths}
                                selectedYear={selectedYear}
                                totals={totals}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    // Tampilan Tabel - MENGGUNAKAN KOMPONEN YANG TELAH DIPISAH
                    <ProductionTable
                        productionSummary={productionSummary}
                        activeMonths={activeMonths}
                        selectedYear={selectedYear}
                        mergeSettings={mergeSettings}
                        debugInfo={debugInfo}
                    />
                )}
            </Paper>

            {/* Dialog Pengaturan Merge & Center - MENGGUNAKAN KOMPONEN YANG TELAH DIPISAH */}
            <MergeSettingsDialog
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                onSave={handleSaveSettings}
                initialSettings={mergeSettings}
            />

            {/* Menu Dropdown Tahun */}
            <Menu
                anchorEl={yearAnchorEl}
                open={Boolean(yearAnchorEl)}
                onClose={handleYearClose}
                PaperProps={{
                    style: {
                        maxHeight: 300,
                    },
                }}
            >
                {yearList.map((year) => (
                    <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                        {year}
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ProductionSummary;